import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function PhotoGallery({ photos, getImageUrl }) {
  const { t } = useTranslation();
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openLightbox = useCallback((index) => setLightboxIndex(index), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  useEffect(() => {
    if (lightboxIndex == null) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight' && lightboxIndex < photos.length - 1) setLightboxIndex((i) => i + 1);
      if (e.key === 'ArrowLeft' && lightboxIndex > 0) setLightboxIndex((i) => i - 1);
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex, photos.length, closeLightbox]);

  if (!photos || photos.length === 0) return null;

  const primary = photos[0];
  const secondary = photos.slice(1, 5);

  return (
    <section className="ri-section ri-gallery" aria-labelledby="ri-gallery-heading">
      <h2 id="ri-gallery-heading" className="ri-section-title">
        <span className="ri-section-icon" aria-hidden="true">📷</span>
        {t('restaurant.info.photos')}
        <span className="ri-gallery-count">{photos.length} {t('restaurant.info.photosCount', 'Photos')}</span>
      </h2>
      <div className="ri-gallery-layout">
        <div className="ri-gallery-primary">
          <button
            type="button"
            className="ri-gallery-primary-btn"
            onClick={() => openLightbox(0)}
            aria-label={t('restaurant.info.viewPhoto', 'View photo')}
          >
            <img src={getImageUrl(primary)} alt="" loading="lazy" className="ri-gallery-img" />
          </button>
        </div>
        {secondary.length > 0 && (
          <div className="ri-gallery-grid">
            {secondary.map((url, idx) => (
              <button
                key={idx}
                type="button"
                className="ri-gallery-thumb-btn"
                onClick={() => openLightbox(idx + 1)}
                aria-label={t('restaurant.info.viewPhoto', 'View photo')}
              >
                <img src={getImageUrl(url)} alt="" loading="lazy" className="ri-gallery-img" />
              </button>
            ))}
          </div>
        )}
      </div>

      {lightboxIndex != null && (
        <div
          className="ri-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={t('restaurant.info.photoGallery', 'Photo gallery')}
          onClick={closeLightbox}
        >
          <button type="button" className="ri-lightbox-close" onClick={closeLightbox} aria-label={t('common.close')}>
            ×
          </button>
          <div className="ri-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <img src={getImageUrl(photos[lightboxIndex])} alt="" className="ri-lightbox-img" />
            {photos.length > 1 && (
              <>
                {lightboxIndex > 0 && (
                  <button
                    type="button"
                    className="ri-lightbox-prev"
                    onClick={() => setLightboxIndex((i) => i - 1)}
                    aria-label={t('common.back')}
                  >
                    ‹
                  </button>
                )}
                {lightboxIndex < photos.length - 1 && (
                  <button
                    type="button"
                    className="ri-lightbox-next"
                    onClick={() => setLightboxIndex((i) => i + 1)}
                    aria-label={t('common.next')}
                  >
                    ›
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
